import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import {
  Monitor, Video, Plus, RefreshCw, Play, CheckCircle2, XCircle,
  Clock, User, ExternalLink, ClipboardCheck, Loader2, Search, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-700',
  active: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

const TOOLS = [
  { id: 'chrome_remote_desktop', name: 'Chrome Remote Desktop' },
  { id: 'anydesk', name: 'AnyDesk' },
  { id: 'teamviewer', name: 'TeamViewer' },
  { id: 'rustdesk', name: 'RustDesk' },
  { id: 'other', name: 'Other / Custom Link' },
];

export default function RemoteSupport() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Remote Support');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [form, setForm] = useState({ ticket_id: '', customer_name: '', tool: 'chrome_remote_desktop', session_link: '', technician_name: '' });

  const fetchSessions = useCallback(async () => {
    try {
      const q = filterStatus ? `?status=${filterStatus}` : '';
      const { data } = await api.get(`/api/remote/sessions${q}`);
      setSessions(data.sessions || []);
    } catch { toast.error('Failed to load sessions'); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleCreate = async () => {
    if (!form.customer_name) { toast.error('Customer name required'); return; }
    try {
      const { data } = await api.post('/api/remote/sessions', form);
      setSessions(prev => [data, ...prev]);
      setShowCreate(false);
      setForm({ ticket_id: '', customer_name: '', tool: 'chrome_remote_desktop', session_link: '', technician_name: '' });
      toast.success('Session created');
    } catch { toast.error('Failed to create session'); }
  };

  const handleStatusChange = async (sessionId, newStatus) => {
    try {
      const { data } = await api.put(`/api/remote/sessions/${sessionId}`, { status: newStatus });
      setSessions(prev => prev.map(s => s.id === sessionId ? data : s));
      if (selected?.id === sessionId) setSelected(data);
      toast.success(`Session ${newStatus}`);
    } catch { toast.error('Update failed'); }
  };

  const handleChecklist = async (sessionId, key, value) => {
    const session = sessions.find(s => s.id === sessionId);
    if (!session) return;
    const checklist = { ...session.checklist, [key]: value };
    try {
      const { data } = await api.put(`/api/remote/sessions/${sessionId}`, { checklist });
      setSessions(prev => prev.map(s => s.id === sessionId ? data : s));
      if (selected?.id === sessionId) setSelected(data);
    } catch { toast.error('Checklist update failed'); }
  };

  const handleNotes = async (sessionId, notes) => {
    try {
      await api.put(`/api/remote/sessions/${sessionId}`, { notes });
      setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, notes } : s));
    } catch (e) { /* handled */ }
  };

  const pendingCount = sessions.filter(s => s.status === 'pending').length;
  const activeCount = sessions.filter(s => s.status === 'active').length;
  const completedCount = sessions.filter(s => s.status === 'completed').length;

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="remote-support-page">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">Remote IT Support</h1>
              <p className="text-sm text-gray-500 mt-1">Start remote sessions, diagnose issues, and verify fixes</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchSessions} className="rounded-lg text-xs" data-testid="remote-refresh">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
              </Button>
              <Button onClick={() => setShowCreate(!showCreate)} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="remote-new-session">
                <Plus className="w-3.5 h-3.5 mr-1" /> New Session
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-5 hover-lift">
              <Monitor className="w-5 h-5 text-[#D4A843] mb-2" />
              <p className="font-heading text-2xl font-bold text-gray-900" data-testid="remote-total-count">{sessions.length}</p>
              <p className="text-xs text-gray-500">Total Sessions</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 hover-lift">
              <Clock className="w-5 h-5 text-amber-600 mb-2" />
              <p className="font-heading text-2xl font-bold text-amber-700" data-testid="remote-pending-count">{pendingCount}</p>
              <p className="text-xs text-amber-600">Pending</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 hover-lift">
              <Play className="w-5 h-5 text-emerald-600 mb-2" />
              <p className="font-heading text-2xl font-bold text-emerald-700" data-testid="remote-active-count">{activeCount}</p>
              <p className="text-xs text-emerald-600">Active</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 hover-lift">
              <CheckCircle2 className="w-5 h-5 text-blue-600 mb-2" />
              <p className="font-heading text-2xl font-bold text-blue-700" data-testid="remote-completed-count">{completedCount}</p>
              <p className="text-xs text-blue-600">Completed</p>
            </div>
          </div>

          {/* Create Form */}
          {showCreate && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 animate-fade-in" data-testid="remote-create-form">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Start Remote Session</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">Customer Name *</Label>
                  <Input value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})} className="mt-1 rounded-lg" data-testid="remote-customer-name" placeholder="Client name..." />
                </div>
                <div>
                  <Label className="text-xs">Linked Ticket ID</Label>
                  <Input value={form.ticket_id} onChange={e => setForm({...form, ticket_id: e.target.value})} className="mt-1 rounded-lg font-mono text-xs" data-testid="remote-ticket-id" placeholder="Optional ticket reference" />
                </div>
                <div>
                  <Label className="text-xs">Remote Tool</Label>
                  <select value={form.tool} onChange={e => setForm({...form, tool: e.target.value})} className="mt-1 w-full h-9 rounded-lg border border-gray-200 px-3 text-sm bg-white" data-testid="remote-tool-select">
                    {TOOLS.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Session Link / Access Code</Label>
                  <Input value={form.session_link} onChange={e => setForm({...form, session_link: e.target.value})} className="mt-1 rounded-lg font-mono text-xs" data-testid="remote-session-link" placeholder="https://remotedesktop.google.com/..." />
                </div>
                <div>
                  <Label className="text-xs">Technician Name</Label>
                  <Input value={form.technician_name} onChange={e => setForm({...form, technician_name: e.target.value})} className="mt-1 rounded-lg" data-testid="remote-tech-name" placeholder="Assigned tech..." />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleCreate} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="remote-create-btn">Create Session</Button>
                <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-lg text-xs" data-testid="remote-cancel-btn">Cancel</Button>
              </div>
            </div>
          )}

          {/* Filter */}
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-400" />
            {['', 'pending', 'active', 'completed', 'cancelled'].map(s => (
              <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1 rounded text-xs font-medium transition-colors ${filterStatus === s ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`remote-filter-${s || 'all'}`}>
                {s || 'All'}
              </button>
            ))}
          </div>

          {/* Session Detail */}
          {selected && (
            <SessionDetail
              session={selected}
              onClose={() => setSelected(null)}
              onStatusChange={handleStatusChange}
              onChecklist={handleChecklist}
              onNotes={handleNotes}
            />
          )}

          {/* Session List */}
          {!selected && (
            <div className="space-y-3" data-testid="remote-session-list">
              {sessions.length === 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                  <Monitor className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No remote sessions yet. Click "New Session" to start.</p>
                </div>
              )}
              {sessions.map(session => (
                <div key={session.id} className="bg-white border border-gray-200 rounded-lg p-5 hover-lift cursor-pointer" onClick={() => setSelected(session)} data-testid={`remote-session-${session.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Video className="w-5 h-5 text-[#D4A843]" />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-gray-900">{session.customer_name || 'Unknown Client'}</p>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${STATUS_BADGE[session.status] || 'bg-gray-100 text-gray-500'}`}>{session.status}</span>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          {session.tool?.replace(/_/g, ' ')} {session.ticket_id ? `| Ticket: ${session.ticket_id.slice(0, 8)}...` : ''}
                          {session.technician_name ? ` | Tech: ${session.technician_name}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {session.session_link && (
                        <a href={session.session_link} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="text-[#D4A843] hover:text-[#E8C972]" data-testid={`remote-link-${session.id}`}>
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <p className="text-[10px] text-gray-400">{new Date(session.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  {/* Checklist progress */}
                  {session.checklist && (
                    <div className="mt-3 flex items-center gap-1">
                      {Object.entries(session.checklist).map(([k, v]) => (
                        <div key={k} className={`w-2 h-2 rounded-full ${v ? 'bg-emerald-500' : 'bg-gray-200'}`} title={k.replace(/_/g, ' ')} />
                      ))}
                      <span className="text-[10px] text-gray-400 ml-1">
                        {Object.values(session.checklist).filter(Boolean).length}/{Object.keys(session.checklist).length} checks
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function SessionDetail({ session, onClose, onStatusChange, onChecklist, onNotes }) {
  const [notes, setNotes] = useState(session.notes || '');
  const [saving, setSaving] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);

  const saveNotes = async () => {
    setSaving(true);
    await onNotes(session.id, notes);
    setSaving(false);
    toast.success('Notes saved');
  };

  const runDiagnosis = async () => {
    if (!notes.trim()) { toast.error('Add session notes first for AI to analyze'); return; }
    setDiagnosing(true);
    try {
      const { data } = await api.post('/api/enhance/ai-diagnosis', {
        notes,
        context: `Customer: ${session.customer_name}, Tool: ${session.tool}, Status: ${session.status}`,
      });
      setDiagnosis(data);
      toast.success('Diagnosis complete');
    } catch { toast.error('AI diagnosis failed'); }
    finally { setDiagnosing(false); }
  };

  const checkItems = [
    { key: 'issue_resolved', label: 'Issue Resolved', desc: 'The original reported issue has been fixed' },
    { key: 'cannot_reproduce', label: 'Cannot Reproduce After Fix', desc: 'Issue cannot be reproduced after the fix was applied' },
    { key: 'performance_normal', label: 'Performance Normal', desc: 'System performance is within expected parameters' },
    { key: 'customer_confirmed', label: 'Customer Confirmed', desc: 'Customer has confirmed the fix is satisfactory' },
  ];

  return (
    <div className="bg-white border border-[#D4A843]/20 rounded-lg p-6 animate-fade-in space-y-5" data-testid="remote-session-detail">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-gray-900">{session.customer_name}</h3>
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${STATUS_BADGE[session.status]}`}>{session.status}</span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5 font-mono">{session.id}</p>
        </div>
        <Button variant="outline" size="sm" onClick={onClose} className="text-xs rounded-lg" data-testid="remote-close-detail"><X className="w-3.5 h-3.5" /></Button>
      </div>

      {/* Session Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-[10px] text-gray-400 uppercase">Tool</p>
          <p className="text-sm font-medium text-gray-900">{session.tool?.replace(/_/g, ' ')}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-[10px] text-gray-400 uppercase">Technician</p>
          <p className="text-sm font-medium text-gray-900">{session.technician_name || 'Unassigned'}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-[10px] text-gray-400 uppercase">Created</p>
          <p className="text-sm font-medium text-gray-900">{new Date(session.created_at).toLocaleString()}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-[10px] text-gray-400 uppercase">Ticket</p>
          <p className="text-sm font-medium text-gray-900 font-mono">{session.ticket_id ? session.ticket_id.slice(0, 12) + '...' : 'None'}</p>
        </div>
      </div>

      {/* Session Link */}
      {session.session_link && (
        <div className="bg-[#D4A843]/5 border border-[#D4A843]/20 rounded-lg p-4 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-[#D4A843] uppercase tracking-wider">Remote Connection</p>
            <p className="text-sm font-mono text-gray-700 mt-1 break-all">{session.session_link}</p>
          </div>
          <a href={session.session_link} target="_blank" rel="noreferrer" data-testid="remote-connect-btn">
            <Button size="sm" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs">
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> Connect
            </Button>
          </a>
        </div>
      )}

      {/* Status Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-gray-500 mr-2">Actions:</span>
        {session.status === 'pending' && (
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" onClick={() => onStatusChange(session.id, 'active')} data-testid="remote-start-btn">
            <Play className="w-3 h-3 mr-1" /> Start Session
          </Button>
        )}
        {session.status === 'active' && (
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs" onClick={() => onStatusChange(session.id, 'completed')} data-testid="remote-complete-btn">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Completed
          </Button>
        )}
        {(session.status === 'pending' || session.status === 'active') && (
          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 rounded-lg text-xs" onClick={() => onStatusChange(session.id, 'cancelled')} data-testid="remote-cancel-session">
            <XCircle className="w-3 h-3 mr-1" /> Cancel
          </Button>
        )}
      </div>

      {/* Post-Fix Verification Checklist */}
      <div>
        <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-[#D4A843]" /> Post-Fix Verification Checklist
        </h4>
        <div className="space-y-2" data-testid="remote-checklist">
          {checkItems.map(item => {
            const checked = session.checklist?.[item.key] || false;
            return (
              <label key={item.key} className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${checked ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'}`} data-testid={`remote-check-${item.key}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={e => onChecklist(session.id, item.key, e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#D4A843] focus:ring-[#D4A843]"
                />
                <div>
                  <p className={`text-sm font-medium ${checked ? 'text-emerald-700' : 'text-gray-900'}`}>{item.label}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{item.desc}</p>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Session Notes */}
      <div>
        <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">Session Notes</h4>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          className="w-full h-24 rounded-lg border border-gray-200 p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#D4A843]"
          placeholder="Diagnosis notes, steps taken, observations..."
          data-testid="remote-notes-input"
        />
        <Button size="sm" variant="outline" className="mt-2 rounded-lg text-xs" onClick={saveNotes} disabled={saving} data-testid="remote-save-notes">
          {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Save Notes
        </Button>
        <Button size="sm" className="mt-2 ml-2 bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" onClick={runDiagnosis} disabled={diagnosing} data-testid="remote-ai-diagnosis-btn">
          {diagnosing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} AI Quick Diagnosis
        </Button>
      </div>

      {/* AI Diagnosis Result */}
      {diagnosis && (
        <div className="bg-[#D4A843]/5 border border-[#D4A843]/20 rounded-lg p-5 space-y-3" data-testid="remote-diagnosis-result">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-[#D4A843] uppercase tracking-wider">AI Diagnosis</h4>
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${diagnosis.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' : diagnosis.confidence === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
              {diagnosis.confidence} confidence
            </span>
          </div>
          <p className="text-sm text-gray-700">{diagnosis.diagnosis}</p>
          {(diagnosis.suggestions || []).length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] text-gray-500 uppercase font-bold">Suggested Fixes:</p>
              {diagnosis.suggestions.map((s, i) => (
                <div key={`item-${i}`} className="flex items-start gap-2 p-2 bg-white rounded border border-gray-200">
                  <span className="text-xs font-bold text-[#D4A843] mt-0.5">{i + 1}.</span>
                  <div>
                    <p className="text-xs font-medium text-gray-900">{s.title}</p>
                    <p className="text-[10px] text-gray-500">{s.step}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
